/**
 * Admin User Management Page - Carbon Design System
 * Comprehensive user management interface for platform administrators
 * Epic 87: Core Platform MVP - Shared Authentication Foundation
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Button,
  Pagination,
  Tag,
  Modal,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
  SelectItem,
  TextInput,
  InlineNotification,
  Loading,
  MultiSelect,
  OverflowMenu,
  OverflowMenuItem,
  Stack,
} from '@carbon/react';
import { View, Edit, UserMultiple, CheckmarkFilled, Misuse } from '@carbon/icons-react';
import axios from 'axios';
import styles from './AdminUserManagementPage.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface User {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  subscription_type: string | null;
  status: string;
  email_verified: boolean;
  mfa_enabled: boolean;
  created_at: number;
  last_login_at: number | null;
}

interface UserSession {
  session_id: string;
  device_name: string | null;
  device_type: string | null;
  ip_address: string | null;
  created_at: number;
  last_active_at: number;
  expires_at: number;
}

interface AuditLog {
  log_id: string;
  event_type: string;
  ip_address: string | null;
  user_agent: string | null;
  metadata: string;
  created_at: number;
}

const AdminUserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<string[]>([]);

  // Modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // User details for view modal
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [subscription, setSubscription] = useState<any>(null);

  // Edit form state
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editSubscriptionType, setEditSubscriptionType] = useState('');

  // Suspend form state
  const [suspendReason, setSuspendReason] = useState('');

  // Notification state
  const [notification, setNotification] = useState<{
    kind: 'success' | 'error' | 'info' | 'warning';
    title: string;
    subtitle: string;
  } | null>(null);

  const headers = [
    { key: 'email', header: 'Email' },
    { key: 'name', header: 'Name' },
    { key: 'role', header: 'Role' },
    { key: 'subscription', header: 'Subscription' },
    { key: 'status', header: 'Status' },
    { key: 'mfa', header: 'MFA' },
    { key: 'created', header: 'Created' },
    { key: 'actions', header: 'Actions' },
  ];

  const roleOptions = [
    { id: 'student', text: 'Student' },
    { id: 'parent', text: 'Parent' },
    { id: 'teacher', text: 'Teacher' },
    { id: 'school_admin', text: 'School Admin' },
    { id: 'district_admin', text: 'District Admin' },
    { id: 'individual', text: 'Individual' },
    { id: 'enterprise_user', text: 'Enterprise User' },
    { id: 'platform_admin', text: 'Platform Admin' },
  ];

  const statusOptions = [
    { id: 'active', text: 'Active' },
    { id: 'suspended', text: 'Suspended' },
    { id: 'deleted', text: 'Deleted' },
  ];

  const subscriptionOptions = [
    { id: 'school', text: 'School' },
    { id: 'family', text: 'Family' },
    { id: 'individual', text: 'Individual' },
    { id: 'enterprise', text: 'Enterprise' },
  ];

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, searchQuery, selectedRole, selectedStatus, selectedSubscriptionType]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: pageSize.toString(),
      });

      if (searchQuery) params.append('search', searchQuery);
      if (selectedRole.length > 0) params.append('role', selectedRole[0]);
      if (selectedStatus.length > 0) params.append('status', selectedStatus[0]);
      if (selectedSubscriptionType.length > 0) params.append('subscription_type', selectedSubscriptionType[0]);

      const response = await axios.get(`${API_BASE_URL}/v1/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUsers(response.data.users || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      setNotification({
        kind: 'error',
        title: 'Failed to load users',
        subtitle: 'Please try again or contact support if the problem persists.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId: string) => {
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/v1/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setUserSessions(response.data.sessions || []);
      setAuditLogs(response.data.audit_logs || []);
      setSubscription(response.data.subscription);
    } catch (error) {
      setNotification({
        kind: 'error',
        title: 'Failed to load user details',
        subtitle: 'Please try again or contact support.',
      });
    }
  };

  const handleViewUser = async (user: User) => {
    setSelectedUser(user);
    await fetchUserDetails(user.user_id);
    setViewModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditRole(user.role);
    setEditStatus(user.status);
    setEditSubscriptionType(user.subscription_type || '');
    setEditModalOpen(true);
  };

  const handleSuspendUser = (user: User) => {
    setSelectedUser(user);
    setSuspendReason('');
    setSuspendModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      await axios.patch(
        `${API_BASE_URL}/v1/admin/users/${selectedUser.user_id}`,
        {
          role: editRole,
          status: editStatus,
          subscription_type: editSubscriptionType || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({
        kind: 'success',
        title: 'User updated successfully',
        subtitle: `Changes have been applied to ${selectedUser.email}`,
      });
      setEditModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Failed to update user',
        subtitle: error.response?.data?.error || 'An error occurred',
      });
    }
  };

  const handleConfirmSuspend = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/v1/admin/users/${selectedUser.user_id}/suspend`,
        { reason: suspendReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({
        kind: 'success',
        title: 'User suspended successfully',
        subtitle: `${selectedUser.email} has been suspended and all sessions have been terminated`,
      });
      setSuspendModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Failed to suspend user',
        subtitle: error.response?.data?.error || 'An error occurred',
      });
    }
  };

  const handleActivateUser = async (user: User) => {
    try {
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/v1/admin/users/${user.user_id}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setNotification({
        kind: 'success',
        title: 'User activated successfully',
        subtitle: `${user.email} has been activated`,
      });
      fetchUsers();
    } catch (error: any) {
      setNotification({
        kind: 'error',
        title: 'Failed to activate user',
        subtitle: error.response?.data?.error || 'An error occurred',
      });
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    // Handle both seconds and milliseconds timestamps
    // If timestamp > 1e12, it's likely already in milliseconds
    const milliseconds = timestamp > 1e12 ? timestamp : timestamp * 1000;
    const date = new Date(milliseconds);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    // Handle both seconds and milliseconds timestamps
    // If timestamp > 1e12, it's likely already in milliseconds
    const milliseconds = timestamp > 1e12 ? timestamp : timestamp * 1000;
    const date = new Date(milliseconds);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'active':
        return <Tag type="green">Active</Tag>;
      case 'suspended':
        return <Tag type="red">Suspended</Tag>;
      case 'deleted':
        return <Tag type="gray">Deleted</Tag>;
      default:
        return <Tag type="gray">{status}</Tag>;
    }
  };

  const rows = users.map(user => ({
    id: user.user_id,
    email: user.email,
    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-',
    role: <Tag type="blue">{user.role}</Tag>,
    subscription: user.subscription_type ? <Tag type="outline">{user.subscription_type}</Tag> : '-',
    status: getStatusTag(user.status),
    mfa: user.mfa_enabled ? (
      <Tag type="green" renderIcon={CheckmarkFilled}>
        Enabled
      </Tag>
    ) : (
      <Tag type="gray">Disabled</Tag>
    ),
    created: formatDate(user.created_at),
    actions: (
      <OverflowMenu size="sm" flipped aria-label={`Actions for user ${user.email}`}>
        <OverflowMenuItem itemText="View Details" onClick={() => handleViewUser(user)} />
        <OverflowMenuItem itemText="Edit User" onClick={() => handleEditUser(user)} />
        {user.status === 'active' ? (
          <OverflowMenuItem itemText="Suspend User" onClick={() => handleSuspendUser(user)} isDelete />
        ) : (
          <OverflowMenuItem itemText="Activate User" onClick={() => handleActivateUser(user)} />
        )}
      </OverflowMenu>
    ),
  }));

  if (loading && users.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Loading description="Loading users..." withOverlay={false} />
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {notification && (
        <InlineNotification
          kind={notification.kind}
          title={notification.title}
          subtitle={notification.subtitle}
          onClose={() => setNotification(null)}
          className={styles.notification}
        />
      )}

      <h1 className={styles.pageHeading}>User Management</h1>
      <p className={styles.pageDescription}>Manage user accounts across all subscription types and roles</p>

      <DataTable rows={rows} headers={headers}>
        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer>
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch
                  placeholder="Search by email, name, or user ID"
                  onChange={(e: any) => setSearchQuery(e.target.value)}
                />
                <MultiSelect
                  id="role-filter"
                  titleText=""
                  label="Filter by Role"
                  items={roleOptions}
                  itemToString={(item: any) => item.text}
                  onChange={({ selectedItems }: any) => setSelectedRole(selectedItems.map((item: any) => item.id))}
                  selectedItems={roleOptions.filter(opt => selectedRole.includes(opt.id))}
                />
                <MultiSelect
                  id="status-filter"
                  titleText=""
                  label="Filter by Status"
                  items={statusOptions}
                  itemToString={(item: any) => item.text}
                  onChange={({ selectedItems }: any) => setSelectedStatus(selectedItems.map((item: any) => item.id))}
                  selectedItems={statusOptions.filter(opt => selectedStatus.includes(opt.id))}
                />
                <MultiSelect
                  id="subscription-filter"
                  titleText=""
                  label="Filter by Subscription"
                  items={subscriptionOptions}
                  itemToString={(item: any) => item.text}
                  onChange={({ selectedItems }: any) =>
                    setSelectedSubscriptionType(selectedItems.map((item: any) => item.id))
                  }
                  selectedItems={subscriptionOptions.filter(opt => selectedSubscriptionType.includes(opt.id))}
                />
              </TableToolbarContent>
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader {...getHeaderProps({ header })} key={header.key}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow {...getRowProps({ row })} key={row.id}>
                    {row.cells.map(cell => (
                      <TableCell key={cell.id}>{cell.value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              pageSizes={[20, 50, 100]}
              totalItems={totalCount}
              onChange={({ page, pageSize }) => {
                setPage(page);
                setPageSize(pageSize);
              }}
            />
          </TableContainer>
        )}
      </DataTable>

      {/* View User Details Modal */}
      <Modal
        open={viewModalOpen}
        onRequestClose={() => setViewModalOpen(false)}
        modalHeading={selectedUser ? `User Details: ${selectedUser.email}` : 'User Details'}
        passiveModal
        size="lg"
      >
        <Tabs>
          <TabList aria-label="User details tabs">
            <Tab>Profile Information</Tab>
            <Tab>Active Sessions ({userSessions.length})</Tab>
            <Tab>Audit Log ({auditLogs.length})</Tab>
            <Tab>Subscription Details</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {selectedUser && (
                <Stack gap={5}>
                  <div>
                    <strong>User ID:</strong> {selectedUser.user_id}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedUser.email}
                  </div>
                  <div>
                    <strong>Name:</strong>{' '}
                    {`${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || 'Not set'}
                  </div>
                  <div>
                    <strong>Role:</strong> {selectedUser.role}
                  </div>
                  <div>
                    <strong>Status:</strong> {getStatusTag(selectedUser.status)}
                  </div>
                  <div>
                    <strong>Email Verified:</strong> {selectedUser.email_verified ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <strong>MFA Enabled:</strong> {selectedUser.mfa_enabled ? 'Yes' : 'No'}
                  </div>
                  <div>
                    <strong>Created:</strong> {formatDateTime(selectedUser.created_at)}
                  </div>
                  <div>
                    <strong>Last Login:</strong> {formatDateTime(selectedUser.last_login_at)}
                  </div>
                </Stack>
              )}
            </TabPanel>
            <TabPanel>
              {userSessions.length === 0 ? (
                <p>No active sessions</p>
              ) : (
                <Stack gap={4}>
                  {userSessions.map(session => (
                    <div key={session.session_id} className={styles.detailCard}>
                      <div>
                        <strong>Device:</strong> {session.device_name || 'Unknown'} ({session.device_type})
                      </div>
                      <div>
                        <strong>IP Address:</strong> {session.ip_address || 'Unknown'}
                      </div>
                      <div>
                        <strong>Last Active:</strong> {formatDateTime(session.last_active_at)}
                      </div>
                      <div>
                        <strong>Expires:</strong> {formatDateTime(session.expires_at)}
                      </div>
                    </div>
                  ))}
                </Stack>
              )}
            </TabPanel>
            <TabPanel>
              {auditLogs.length === 0 ? (
                <p>No audit logs found</p>
              ) : (
                <Stack gap={4}>
                  {auditLogs.slice(0, 20).map(log => (
                    <div key={log.log_id} className={styles.detailCard}>
                      <div>
                        <strong>Event:</strong> {log.event_type}
                      </div>
                      <div>
                        <strong>IP Address:</strong> {log.ip_address || 'Unknown'}
                      </div>
                      <div>
                        <strong>Time:</strong> {formatDateTime(log.created_at)}
                      </div>
                    </div>
                  ))}
                </Stack>
              )}
            </TabPanel>
            <TabPanel>
              {subscription ? (
                <Stack gap={5}>
                  <div>
                    <strong>Plan:</strong> {subscription.plan_name}
                  </div>
                  <div>
                    <strong>Status:</strong> {subscription.status}
                  </div>
                  <div>
                    <strong>Billing Interval:</strong> {subscription.billing_interval}
                  </div>
                  <div>
                    <strong>Price:</strong> ${(subscription.price_cents / 100).toFixed(2)} {subscription.currency}
                  </div>
                </Stack>
              ) : (
                <p>No subscription found for this user</p>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editModalOpen}
        onRequestClose={() => setEditModalOpen(false)}
        modalHeading={selectedUser ? `Edit User: ${selectedUser.email}` : 'Edit User'}
        primaryButtonText="Save Changes"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSaveEdit}
      >
        <Stack gap={5}>
          <Select id="edit-role" labelText="Role" value={editRole} onChange={e => setEditRole(e.target.value)}>
            {roleOptions.map(role => (
              <SelectItem key={role.id} value={role.id} text={role.text} />
            ))}
          </Select>
          <Select id="edit-status" labelText="Status" value={editStatus} onChange={e => setEditStatus(e.target.value)}>
            {statusOptions.map(status => (
              <SelectItem key={status.id} value={status.id} text={status.text} />
            ))}
          </Select>
          <Select
            id="edit-subscription"
            labelText="Subscription Type"
            value={editSubscriptionType}
            onChange={e => setEditSubscriptionType(e.target.value)}
          >
            <SelectItem value="" text="None" />
            {subscriptionOptions.map(sub => (
              <SelectItem key={sub.id} value={sub.id} text={sub.text} />
            ))}
          </Select>
        </Stack>
      </Modal>

      {/* Suspend User Modal */}
      <Modal
        open={suspendModalOpen}
        onRequestClose={() => setSuspendModalOpen(false)}
        modalHeading={selectedUser ? `Suspend User: ${selectedUser.email}` : 'Suspend User'}
        primaryButtonText="Suspend User"
        secondaryButtonText="Cancel"
        danger
        onRequestSubmit={handleConfirmSuspend}
      >
        <Stack gap={5}>
          <p>Are you sure you want to suspend this user? This will:</p>
          <ul>
            <li>Set the user's status to 'suspended'</li>
            <li>Invalidate all active sessions</li>
            <li>Prevent the user from logging in</li>
            <li>Send a notification email to the user</li>
          </ul>
          <TextInput
            id="suspend-reason"
            labelText="Reason for suspension"
            placeholder="Enter reason"
            value={suspendReason}
            onChange={e => setSuspendReason(e.target.value)}
          />
        </Stack>
      </Modal>
    </div>
  );
};

export default AdminUserManagementPage;
